import React, { useEffect, useMemo, useState } from 'react'
import { Button, Card, Container, Form } from 'react-bootstrap'
import { BsArrowLeft } from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import Cookies from 'js-cookie'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import { BASE_URL } from './commonComponents/ApiEndpoints'

const formatTemplateType = (type) => {
  if (!type) return '-'
  return String(type)
}

const getTemplateLabel = (item) => {
  if (!item) return ''
  return item.name || `Template #${item.id}`
}

export default function EditTemplate() {
  const { logout, user } = useAuth()
  const { isCollapsed } = useSidebar()
  const { logo } = useInstitute()
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const [template, setTemplate] = useState(location.state?.template || null)
  const [selected, setSelected] = useState(
    Number(location.state?.template?.selected) === 1,
  )
  const [templateName, setTemplateName] = useState(
    location.state?.template?.name ||
      location.state?.template?.template_name ||
      '',
  )
  const [templateOptions, setTemplateOptions] = useState([])
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [loading, setLoading] = useState(!location.state?.template)
  const [saving, setSaving] = useState(false)
  const [logoError, setLogoError] = useState(false)
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const getAuthToken = () => {
    if (user?.token) return user.token
    try {
      const userCookie = Cookies.get('user')
      return userCookie ? JSON.parse(userCookie)?.token : ''
    } catch {
      return ''
    }
  }

  const filteredTemplateOptions = useMemo(() => {
    if (!template?.template_type) return []
    return templateOptions.filter(
      (item) => String(item.template_type) === String(template.template_type),
    )
  }, [template?.template_type, templateOptions])

  const resolveTemplateName = (currentTemplate, matchedOption) => {
    return (
      currentTemplate?.name ||
      currentTemplate?.template_name ||
      matchedOption?.name ||
      ''
    )
  }

  const preselectTemplate = (options, currentTemplate) => {
    const existingId =
      currentTemplate?.template_id ??
      currentTemplate?.selected_template_id ??
      null

    if (existingId !== null && existingId !== undefined && existingId !== '') {
      const matched = options.find((item) => String(item.id) === String(existingId))
      if (matched) {
        setSelectedTemplateId(String(matched.id))
        setTemplateName((prev) => prev || resolveTemplateName(currentTemplate, matched))
        return
      }
    }

    setSelectedTemplateId('')
    setTemplateName((prev) => prev || resolveTemplateName(currentTemplate, null))
  }

  const handleTemplateSelect = (value) => {
    setSelectedTemplateId(value)
    const matched = filteredTemplateOptions.find(
      (item) => String(item.id) === String(value),
    )
    if (matched?.name) {
      setTemplateName(matched.name)
    }
  }

  useEffect(() => {
    const fetchAllTemplates = async () => {
      const instituteId = Number(Cookies.get('institute_id'))
      const token = getAuthToken()

      if (!Number.isFinite(instituteId) || instituteId <= 0 || !token) {
        return
      }

      try {
        setLoadingTemplates(true)
        const response = await axios.get(`${BASE_URL}/templates/all`, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
            'Institute-Id': instituteId,
          },
        })

        const options = Array.isArray(response.data?.data) ? response.data.data : []
        setTemplateOptions(options)
        if (template) {
          const filtered = options.filter(
            (item) => String(item.template_type) === String(template.template_type),
          )
          preselectTemplate(filtered, template)
        }
      } catch (error) {
        setTemplateOptions([])
        toast.error(error.response?.data?.message || 'Failed to fetch templates')
      } finally {
        setLoadingTemplates(false)
      }
    }

    fetchAllTemplates()
  }, [])

  useEffect(() => {
    if (location.state?.template) {
      if (filteredTemplateOptions.length > 0) {
        preselectTemplate(filteredTemplateOptions, location.state.template)
      }
      return
    }

    const fetchTemplate = async () => {
      const instituteId = Number(Cookies.get('institute_id'))
      const token = getAuthToken()

      if (!Number.isFinite(instituteId) || instituteId <= 0 || !token) {
        toast.error('Unable to load template. Please try again.')
        navigate('/Templates')
        return
      }

      try {
        setLoading(true)
        const response = await axios.get(`${BASE_URL}/institute-templates`, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
            'Institute-Id': instituteId,
          },
        })

        const matched = (Array.isArray(response.data?.data) ? response.data.data : []).find(
          (item) => String(item.id) === String(id),
        )

        if (!matched) {
          toast.error('Template not found')
          navigate('/Templates')
          return
        }

        setTemplate(matched)
        setSelected(Number(matched.selected) === 1)
        setTemplateName(resolveTemplateName(matched, null))
        const filtered = templateOptions.filter(
          (item) => String(item.template_type) === String(matched.template_type),
        )
        if (filtered.length > 0) {
          preselectTemplate(filtered, matched)
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to load template')
        navigate('/Templates')
      } finally {
        setLoading(false)
      }
    }

    fetchTemplate()
  }, [id, location.state, navigate])

  useEffect(() => {
    if (template && filteredTemplateOptions.length > 0 && !selectedTemplateId) {
      preselectTemplate(filteredTemplateOptions, template)
    }
  }, [template, filteredTemplateOptions, selectedTemplateId])

  const handleSave = async (e) => {
    e.preventDefault()
    if (!template) return

    const trimmedName = templateName.trim()
    if (!trimmedName) {
      toast.error('Please enter a template name')
      return
    }

    const chosen = filteredTemplateOptions.find(
      (item) => String(item.id) === String(selectedTemplateId),
    )
    if (!chosen?.id) {
      toast.error('Please select a template')
      return
    }

    const instituteId = Number(Cookies.get('institute_id'))
    const token = getAuthToken()

    if (!Number.isFinite(instituteId) || instituteId <= 0 || !token) {
      toast.error('User session not found. Please login again.')
      return
    }

    try {
      setSaving(true)
      const response = await axios.put(
        `${BASE_URL}/institute-templates`,
        {
          institute_id: instituteId,
          template_type: template.template_type,
          selected: selected ? 1 : 0,
          template_id: chosen.id,
          name: trimmedName,
        },
        {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
            'Institute-Id': instituteId,
            'Content-Type': 'application/json',
          },
        },
      )

      toast.success(response.data?.message || 'Template updated successfully')
      navigate('/Templates')
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update template')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        <div
          className="container-fluid p-0"
          style={{
            marginTop: typeof window !== 'undefined' && window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                overflow: 'visible',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError && logo ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>
                <h3
                  className="m-0 fw-bold"
                  style={{
                    letterSpacing: '-0.02em',
                    overflow: 'visible',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 'clamp(18px, 2.5vw, 22px)',
                    color: '#111827',
                    lineHeight: '1.2',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  Edit Template
                </h3>
              </div>

              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        <Container
          fluid
          className="mt-4"
          style={{
            paddingLeft: isMobile ? '12px' : isCollapsed ? '32px' : '40px',
            paddingRight: isMobile ? '12px' : '32px',
            paddingTop: '32px',
            paddingBottom: '32px',
            maxWidth: '100%',
          }}
        >
          <Card
            style={{
              border: '1px solid #D1D3D4',
              borderRadius: '12px',
              backgroundColor: '#ffffff',
              width: '100%',
              maxWidth: '100%',
            }}
          >
            <Card.Body className="p-4">
              {loading ? (
                <p className="mb-0" style={{ color: '#6b7280' }}>Loading template...</p>
              ) : (
                <Form onSubmit={handleSave}>
                  {/* <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold" style={{ color: '#6b7280', fontSize: '13px' }}>
                      Template ID
                    </Form.Label>
                    <Form.Control value={template?.id || ''} disabled />
                  </Form.Group> */}

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold" style={{ color: '#6b7280', fontSize: '13px' }}>
                      Template Type
                    </Form.Label>
                    <Form.Control
                      value={formatTemplateType(template?.template_type)}
                      disabled
                    />
                  </Form.Group>

                  {/* <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold" style={{ color: '#6b7280', fontSize: '13px' }}>
                      Template Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      id="edit-template-name"
                      placeholder="Enter template name"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #D1D3D4',
                        fontSize: '14px',
                        minHeight: '40px',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    />
                  </Form.Group> */}

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold" style={{ color: '#6b7280', fontSize: '13px' }}>
                      Selected Template
                    </Form.Label>
                    <Form.Select
                      id="edit-template-select"
                      value={selectedTemplateId}
                      onChange={(e) => handleTemplateSelect(e.target.value)}
                      disabled={loadingTemplates || filteredTemplateOptions.length === 0}
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #D1D3D4',
                        fontSize: '14px',
                        minHeight: '40px',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      <option value="">
                        {loadingTemplates
                          ? 'Loading templates...'
                          : filteredTemplateOptions.length
                            ? 'Select a template'
                            : 'No templates found'}
                      </option>
                      {filteredTemplateOptions.map((item) => (
                        <option key={item.id} value={String(item.id)}>
                          {getTemplateLabel(item)}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Check
                      type="switch"
                      id="template-selected"
                      label="Mark as selected / active"
                      checked={selected}
                      onChange={(e) => setSelected(e.target.checked)}
                    />
                  </Form.Group>

                  <div className="d-flex gap-2">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      onClick={() => navigate('/Templates')}
                      disabled={saving}
                      className="d-flex align-items-center gap-2"
                    >
                      <BsArrowLeft size={16} />
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={saving || loadingTemplates}
                      style={{ backgroundColor: '#223843', borderColor: '#223843' }}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
    </>
  )
}
